import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Transition} from 'react-transition-group';
import ComponentLoading from '../../../../ComponentLoading';
import TransactionTable from '../../tabComponents/Transactions/TransactionTable';
import LoadMoreButton from '../../../TransactionsPage/LoadMoreButton';
import TransactionHeaderDetails from '../../tabComponents/Transactions/TransactionHeaderDetails';
import {StyledTransactionsTabContainer, StyledTableContainer, StyledTable} from './StyledTransactionTab';
import TransactionDetailsPanel from '../../../TransactionsPage/TransactionDetailsPanel';
import TransactionDetails from '../../../TransactionsPage/TransactionDetails/TransactionDetails';
import {elementHelpers, transactionsHelpers} from '../../../../../utils/helpers';
import {invoicesActions} from '../../../../../state/actions/invoice.actions';
import {SYSTEM_ANIMATION_DURATION} from '../../../../../constants';

const tableElementId = 'transactions-table';

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION});

const TransactionsTab = ({
  loading = false,
  loadMoreProps,
  transactions,
  employees,
  edit = true,
  expenses,
  onExpenseUpdate,
  tableProps,
  subscription,
  unlinkExpense,
}) => {
  const [t] = useTranslation(['main', 'transactions']);
  const [storedTransactions, setStoredTransactions] = useState([]);
  const [storedExpenses, setStoredExpenses] = useState([]);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isFixedDetailsWindow, setIsFixedDetailsWindow] = useState(false);
  const [detailsWindowFixedHeight, setDetailsWindowFixedHeight] = useState(0);
  const [selectedRowIndex, setSelectedRowIndex] = useState(undefined);

  const tableNodeRef = useRef(null);
  const detailsNodeRef = useRef(null);

  useEffect(() => {
    let offsetTop = elementHelpers.getElementOffsetTop(`#${tableElementId}`, 4) - 85;
    if (offsetTop !== detailsWindowFixedHeight) setDetailsWindowFixedHeight(offsetTop);
  }, [isFixedDetailsWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    transactionsHelpers.addFixedDetailsWindowScrollEventListener({
      detailsWindowFixedHeight, isOpenDetails, isFixedDetailsWindow, setIsFixedDetailsWindow
    });
  }, [isOpenDetails, isFixedDetailsWindow, detailsWindowFixedHeight]);

  const getGroupedTransactions = (transactions) => {
    const daysList = {};
    const datePropName = 'created_date';
    const summarizeKeyName = 'isYearlyGrouping';
    let groupedTransactions = [];
    let lastAdded;

    // define transactions months
    transactions.forEach(transaction => {
      const date = transaction[datePropName].slice(0, 4);
      const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
      let {amount, is_incoming: isIncoming} = transaction;
      amount = isIncoming ? amount : -amount;
      if (!isReturnedTransaction) {
        if (daysList.hasOwnProperty(date)) {
          daysList[date] = {
            amount: daysList[date].amount + amount
          }
        } else {
          daysList[date] = {amount}
        }
      }
    });

    // add grouping rows to transactions list
    transactions.forEach(t => {
      const date = t[datePropName].slice(0, 4);
      if (lastAdded !== date && daysList.hasOwnProperty(date)) {
        lastAdded = date;
        groupedTransactions.push({
          isYearlyGrouping: true,
          [datePropName]: date,
          ...daysList[date]
        });
      }
      groupedTransactions.push(t);
    });
    groupedTransactions = transactionsHelpers.additionalTransactionsGrouping(groupedTransactions, summarizeKeyName);
    return groupedTransactions;
  }

  useEffect(() => {
    let storedTransactions = transactions.map(t => {
      const user = employees.find(e => e.employee_id === t.user_id);
      const expense = storedExpenses.find(e => e.id === t.expense_id);
      return {
        ...t,
        expense,
        user
      }
    });
    storedTransactions = getGroupedTransactions(storedTransactions);
    setStoredTransactions(storedTransactions);
  }, [employees, transactions, storedExpenses]);

  useEffect(() => {
    setStoredExpenses(expenses);
  }, [expenses]);

  const handleOnExpenseUpdate = (updatedExpense, actionType) => {
    if (updatedExpense) {
      let updatedExpenses = transactionsHelpers.getUpdatedBatchExpensesList(storedExpenses, updatedExpense, actionType);
      setSelectedTransaction({...selectedTransaction, expense: updatedExpense});
      setStoredExpenses(updatedExpenses);
      onExpenseUpdate && onExpenseUpdate(updatedExpense, actionType);
    }
  }

  const handleOpenDetails = (transactionId, index) => {
    const transaction = storedTransactions.find(d => d.id === transactionId);
    setSelectedTransaction(transaction);
    setIsOpenDetails(true);
    selectedRowIndex !== index && setSelectedRowIndex(index);
  }

  const handleCloseDetails = () => {
    setIsOpenDetails(false);
    selectedRowIndex && setSelectedRowIndex(undefined);
  }

  const handleInvoiceRemove = (successCallback, errorCallback) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: selectedTransaction,
      successCallback,
      errorCallback,
      unlinkExpense,
      onExpenseUpdate
    });
  }

  return (
    <StyledTransactionsTabContainer>
      <TransactionHeaderDetails
        data={subscription}
        transactionsCount={transactions.length}
      />
      <StyledTableContainer>
        <Transition nodeRef={tableNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
          {state => (
            <StyledTable
              id={tableElementId}
              style={{
                ...tableStyles.default,
                ...tableStyles[state]
              }}
            >
              <TransactionTable
                totalSpend={subscription?.ytg || 0}
                data={storedTransactions}
                edit={edit}
                onExpenseUpdate={onExpenseUpdate}
                onRowClick={handleOpenDetails}
                selectedRowIndex={selectedRowIndex}
                {...tableProps}
              />

              <LoadMoreButton
                {...loadMoreProps}
              >
                {t('loadMore')}
              </LoadMoreButton>
            </StyledTable>
          )}
        </Transition>
        <Transition nodeRef={detailsNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
          {state => (
            <TransactionDetailsPanel
              headerRightContent={transactionsHelpers.getTransactionDetailsRightContent({ transaction: selectedTransaction, t })}
              onBack={handleCloseDetails}
              fixed={isFixedDetailsWindow}
              style={{
                ...detailsStyles.default,
                ...detailsStyles[state]
              }}
            >
              <TransactionDetails
                edit={edit}
                onExpenseUpdate={handleOnExpenseUpdate}
                onInvoiceRemove={handleInvoiceRemove}
                subscription={subscription}
                transaction={selectedTransaction}
              />
            </TransactionDetailsPanel>
          )}
        </Transition>
      </StyledTableContainer>

      {loading && <ComponentLoading />}
    </StyledTransactionsTabContainer>
  );
}

TransactionsTab.propTypes = {
  edit: PropTypes.bool,
  expenses: PropTypes.array,
  loading: PropTypes.bool,
  loadMoreProps: PropTypes.object,
  onExpenseUpdate: PropTypes.func,
  subscription: PropTypes.object,
  tableProps: PropTypes.object,
  transactions: PropTypes.array
}

const mapStateToProps = state => {
  const {employees} = state.company;

  return {
    employees
  }
}

const mapDispatchToProps = {
  unlinkExpense: invoicesActions.unlinkExpense,
}
export default connect(mapStateToProps, mapDispatchToProps)(TransactionsTab);
